import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Users, 
  Trophy, 
  BookOpen,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock,
  XCircle,
  Shield,
  UserCog,
  Trash2
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function AdminDashboardPage() {
  const [staffSummary, setStaffSummary] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [sops, setSops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userProgress, setUserProgress] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [summaryRes, sopsRes, usersRes] = await Promise.all([
        apiClient.get("/admin/progress-summary"),
        apiClient.get("/sops"),
        apiClient.get("/admin/users"),
      ]);
      setStaffSummary(summaryRes.data);
      setSops(sopsRes.data);
      setAllUsers(usersRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProgress = async (userId) => {
    if (userProgress[userId]) {
      setExpandedUser(expandedUser === userId ? null : userId);
      return;
    }

    try {
      const response = await apiClient.get(`/admin/user-progress/${userId}`);
      setUserProgress((prev) => ({ ...prev, [userId]: response.data }));
      setExpandedUser(userId);
    } catch (error) {
      console.error("Error fetching user progress:", error);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await apiClient.put(`/admin/users/${userId}/role`, { role: newRole });
      setAllUsers(users => users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success(`Role updated to ${newRole}`);
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error("Failed to update role");
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    try {
      await apiClient.delete(`/admin/users/${userId}`);
      setAllUsers(users => users.filter(u => u.id !== userId));
      setStaffSummary(summary => summary.filter(s => s.user_id !== userId));
      toast.success(`${userName} has been removed`);
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin": return "bg-[var(--rd-green)] text-white";
      case "supervisor": return "bg-[var(--rd-gold)] text-white";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const totalStaff = staffSummary.length;
  const fullyTrained = staffSummary.filter((s) => s.completed_sops === s.total_sops).length;
  const avgCompletion = totalStaff > 0
    ? Math.round(staffSummary.reduce((sum, s) => sum + (s.completed_sops / s.total_sops) * 100, 0) / totalStaff)
    : 0;
  const avgQuizScore = staffSummary.filter((s) => s.average_quiz_score !== null).length > 0
    ? Math.round(
        staffSummary.filter((s) => s.average_quiz_score !== null)
          .reduce((sum, s) => sum + s.average_quiz_score, 0) /
        staffSummary.filter((s) => s.average_quiz_score !== null).length
      )
    : null;

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6 max-w-7xl mx-auto">
          <div className="skeleton h-12 w-64"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton h-28"></div>
            ))}
          </div>
          <div className="skeleton h-96 w-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in-up max-w-7xl mx-auto">
        {/* Header */}
        <div>
          <h1 className="text-2xl lg:text-3xl text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
            Team Management
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">
            Monitor your team's training progress and manage user access.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="stat-card" data-testid="admin-stat-total-staff">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-[hsl(var(--muted-foreground))]">
                  Team Members
                </p>
                <p className="text-4xl font-semibold text-[var(--rd-green)] mt-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  {totalStaff}
                </p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[var(--rd-green)]/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-[var(--rd-green)]" />
              </div>
            </div>
          </div>

          <div className="stat-card" data-testid="admin-stat-fully-trained">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-[hsl(var(--muted-foreground))]">
                  Fully Trained
                </p>
                <p className="text-4xl font-semibold text-[var(--rd-green)] mt-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  {fullyTrained}
                </p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-green-50 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="stat-card" data-testid="admin-stat-avg-completion">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-[hsl(var(--muted-foreground))]">
                  Avg. Completion
                </p>
                <p className="text-4xl font-semibold text-[var(--rd-green)] mt-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  {avgCompletion}%
                </p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-blue-50 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="stat-card" data-testid="admin-stat-avg-score">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-[hsl(var(--muted-foreground))]">
                  Avg. Score
                </p>
                <p className="text-4xl font-semibold text-[var(--rd-green)] mt-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  {avgQuizScore !== null ? `${avgQuizScore}%` : "-"}
                </p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[var(--rd-gold)]/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-[var(--rd-gold)]" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs for Progress and User Management */}
        <Tabs defaultValue="progress" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md mb-6">
            <TabsTrigger value="progress" className="data-[state=active]:bg-[var(--rd-green)] data-[state=active]:text-white">
              <BookOpen className="w-4 h-4 mr-2" />
              Training Progress
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[var(--rd-green)] data-[state=active]:text-white">
              <UserCog className="w-4 h-4 mr-2" />
              User Access
            </TabsTrigger>
          </TabsList>

          {/* Training Progress Tab */}
          <TabsContent value="progress">
            <Card className="border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="text-lg text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
                  Individual Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-center">Progress</TableHead>
                      <TableHead className="text-center">Completed</TableHead>
                      <TableHead className="text-center">In Progress</TableHead>
                      <TableHead className="text-center">Avg. Score</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {staffSummary.map((staff) => {
                      const progress = userProgress[staff.user_id] || [];
                      const user = allUsers.find(u => u.id === staff.user_id);
                      
                      return (
                        <Collapsible key={staff.user_id} asChild>
                          <>
                            <TableRow className="cursor-pointer hover:bg-[hsl(var(--muted))]">
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-2">
                                  {staff.user_name}
                                  {user?.role && user.role !== "staff" && (
                                    <span className={`text-xs px-2 py-0.5 rounded ${getRoleBadgeColor(user.role)}`}>
                                      {user.role}
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-[hsl(var(--muted-foreground))]">
                                {staff.user_email}
                              </TableCell>
                              <TableCell className="text-center">
                                <span className={`font-medium ${
                                  (staff.completed_sops / staff.total_sops) >= 1 ? "text-green-600" :
                                  (staff.completed_sops / staff.total_sops) >= 0.5 ? "text-[var(--rd-gold)]" : "text-[hsl(var(--muted-foreground))]"
                                }`}>
                                  {Math.round((staff.completed_sops / staff.total_sops) * 100)}%
                                </span>
                              </TableCell>
                              <TableCell className="text-center text-green-600">
                                {staff.completed_sops}
                              </TableCell>
                              <TableCell className="text-center text-[var(--rd-gold)]">
                                {staff.in_progress_sops}
                              </TableCell>
                              <TableCell className="text-center">
                                {staff.average_quiz_score !== null ? `${Math.round(staff.average_quiz_score)}%` : "-"}
                              </TableCell>
                              <CollapsibleTrigger asChild>
                                <TableCell>
                                  <button
                                    onClick={() => fetchUserProgress(staff.user_id)}
                                    className="p-2 hover:bg-[hsl(var(--muted))] rounded"
                                  >
                                    {expandedUser === staff.user_id ? (
                                      <ChevronUp className="w-4 h-4" />
                                    ) : (
                                      <ChevronDown className="w-4 h-4" />
                                    )}
                                  </button>
                                </TableCell>
                              </CollapsibleTrigger>
                            </TableRow>
                            <CollapsibleContent asChild>
                              <TableRow>
                                <TableCell colSpan={7} className="bg-[hsl(var(--muted))] p-0">
                                  {expandedUser === staff.user_id && (
                                    <div className="p-4">
                                      <h4 className="text-sm font-semibold mb-3">Module Details</h4>
                                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {sops.map((sop) => {
                                          const sopProgress = progress.find((p) => p.sop_id === sop.id);
                                          const isCompleted = sopProgress?.completed;
                                          const isStarted = sopProgress?.completed_steps?.length > 0;

                                          return (
                                            <div
                                              key={sop.id}
                                              className={`p-3 rounded-sm border ${
                                                isCompleted
                                                  ? "bg-green-50 border-green-200"
                                                  : isStarted
                                                  ? "bg-amber-50 border-amber-200"
                                                  : "bg-white border-[hsl(var(--border))]"
                                              }`}
                                            >
                                              <div className="flex items-start gap-2">
                                                {isCompleted ? (
                                                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" />
                                                ) : isStarted ? (
                                                  <Clock className="w-4 h-4 text-amber-600 mt-0.5" />
                                                ) : (
                                                  <XCircle className="w-4 h-4 text-gray-300 mt-0.5" />
                                                )}
                                                <div>
                                                  <p className="text-sm font-medium">{sop.title}</p>
                                                  {sopProgress && (
                                                    <p className="text-xs text-[hsl(var(--muted-foreground))]">
                                                      {sopProgress.quiz_score !== null && `Score: ${sopProgress.quiz_score}%`}
                                                    </p>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                            </CollapsibleContent>
                          </>
                        </Collapsible>
                      );
                    })}
                  </TableBody>
                </Table>

                {staffSummary.length === 0 && (
                  <div className="text-center py-12 text-[hsl(var(--muted-foreground))]">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No staff members have started training yet.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Access Management Tab */}
          <TabsContent value="users">
            <Card className="border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="text-lg text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
                  User Access Management
                </CardTitle>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  Set user roles: <strong>Staff</strong> (training only), <strong>Supervisor</strong> (training + team view), <strong>Admin</strong> (full access)
                </p>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-center">Current Role</TableHead>
                      <TableHead className="text-center">Change Role</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className="text-[hsl(var(--muted-foreground))]">{user.email}</TableCell>
                        <TableCell className="text-center">
                          <span className={`text-xs px-3 py-1 rounded-full ${getRoleBadgeColor(user.role)}`}>
                            {user.role || "staff"}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Select
                            value={user.role || "staff"}
                            onValueChange={(value) => handleRoleChange(user.id, value)}
                          >
                            <SelectTrigger className="w-32 mx-auto">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="staff">Staff</SelectItem>
                              <SelectItem value="supervisor">Supervisor</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-center">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove {user.name}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete this user and all their training progress. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteUser(user.id, user.name)}
                                  className="bg-red-500 hover:bg-red-600"
                                >
                                  Remove User
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {allUsers.length === 0 && (
                  <div className="text-center py-12 text-[hsl(var(--muted-foreground))]">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No users registered yet.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
